import React, { useState, useEffect, useMemo, useCallback } from "react";
import { adminAPI } from "../services/auth";
import { useToasts } from "../hooks/useToasts";
import Spinner from "../components/Spinner";
import Button from "../components/Button";
import Filter from "../components/Filter";

import "./SuperAdmin.css";

const SuperAdmin = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [sortColumn, setSortColumn] = useState("email");
  const [sortDirection, setSortDirection] = useState("asc");
  const { addToast } = useToasts();

  const sortedUsers = useMemo(() => {
    const filtered = users.filter((user) => {
      // SuperAdmin sees ONLY admins and superadmins
      if (user.role !== "admin" && user.role !== "superadmin") return false;

      const isBlocked =
        user.lockedUntil && new Date(user.lockedUntil) > new Date();
      const roleMatch = roleFilter === "all" || user.role === roleFilter;
      const statusMatch =
        statusFilter === "all" ||
        (statusFilter === "active" && !isBlocked) ||
        (statusFilter === "blocked" && isBlocked);
      return roleMatch && statusMatch;
    });

    return [...filtered].sort((a, b) => {
      let aVal, bVal;

      switch (sortColumn) {
        case "email":
          aVal = (a.email || "").toLowerCase();
          bVal = (b.email || "").toLowerCase();
          break;
        case "index":
          aVal = a.index || 0;
          bVal = b.index || 0;
          break;

        case "createdAt":
          aVal = new Date(a.createdAt || 0);
          bVal = new Date(b.createdAt || 0);
          break;

        case "lastLoginAt":
          aVal = new Date(a.lastLoginAt || 0);
          bVal = new Date(b.lastLoginAt || 0);
          break;
        case "role":
          aVal = a.role || "";
          bVal = b.role || "";
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [users, sortColumn, sortDirection, roleFilter, statusFilter]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getAllUsers();
      setUsers(res.data.users);
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to load users",
        "error-warning"
      );
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await adminAPI.changeUserRole(userId, newRole);
      setUsers(
        users.map((u) => (u._id === userId ? { ...u, role: newRole } : u))
      );
      addToast(`Role changed to ${newRole}`, "success-info");
    } catch (error) {
      const errorData = error.response?.data;
      addToast(errorData?.details?.[0] || "Failed to change role", "error-warning");
    }
  };

  const handleBlockToggle = async (userId, isBlocked) => {
    try {
      await adminAPI.blockUser(userId, !isBlocked);
      const newLockedUntil = !isBlocked
        ? new Date(Date.now() + 100 * 365 * 24 * 60 * 60 * 1000)
        : null;
      setUsers(
        users.map((u) =>
          u._id === userId ? { ...u, lockedUntil: newLockedUntil } : u
        )
      );
      addToast(`Admin ${!isBlocked ? "blocked" : "unblocked"}`, "success-info");
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to update user",
        "error-warning"
      );
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  if (loading) return <Spinner />;

  const handleResetFilters = () => {
    setRoleFilter("all");
    setStatusFilter("all");
  };

  const hasActiveFilters = roleFilter !== "all" || statusFilter !== "all";

  return (
    <div className="form-content-frame table-page">
      <div className="superadmin-header">
        <h1>Admins</h1>
        <div className="superadmin-header-actions">
          <Button
            className={`button-reset${
              hasActiveFilters ? " button-reset-visible" : ""
            }`}
            onClick={handleResetFilters}
          >
            Reset Filters
          </Button>
        </div>
      </div>
      <div className="superadmin-table-container">
        <table className="superadmin-table">
          <thead>
            <tr>
              <th className="header-cell">
                <div className="header-title">No</div>
              </th>
              <th
                onClick={() => handleSort("email")}
                className="header-cell-sortable"
              >
                <div className="header-title">Email</div>
                <div className="header-arrows">
                  {sortColumn === "email"
                    ? sortDirection === "asc"
                      ? "↑↓"
                      : "↓↑"
                    : "↑↓"}
                </div>
              </th>
              <th
                onClick={() => handleSort("index")}
                className="header-cell-sortable"
              >
                <div className="header-title">Index User ID</div>
                <div className="header-arrows">
                  {sortColumn === "index"
                    ? sortDirection === "asc"
                      ? "↑↓"
                      : "↓↑"
                    : "↑↓"}
                </div>
              </th>
              <th className="header-cell">
                <div className="header-title">Status</div>
                <Filter
                  className="header-filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="blocked">Blocked</option>
                </Filter>
              </th>
              <th
                onClick={() => handleSort("createdAt")}
                className="header-cell-sortable"
              >
                <div className="header-title">Created</div>
                <div className="header-arrows">
                  {sortColumn === "createdAt"
                    ? sortDirection === "asc"
                      ? "↑↓"
                      : "↓↑"
                    : "↑↓"}
                </div>
              </th>

              <th
                onClick={() => handleSort("lastLoginAt")}
                className="header-cell-sortable"
              >
                <div className="header-title">Last Login</div>
                <div className="header-arrows">
                  {sortColumn === "lastLoginAt"
                    ? sortDirection === "asc"
                      ? "↑↓"
                      : "↓↑"
                    : "↑↓"}
                </div>
              </th>
              <th className="header-cell">
                <div className="header-title">Role</div>
                <Filter
                  className="header-filter"
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="admin">Admin</option>
                  <option value="superadmin">SuperAdmin</option>
                </Filter>
              </th>
              <th className="header-cell">
                <div className="header-title">Change Role</div>
              </th>
              <th className="header-cell">
                <div className="header-title">Block Unblock</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.length === 0 ? (
              <tr>
                <td colSpan="9" className="superadmin-empty-message">
                  No users found
                </td>
              </tr>
            ) : (
              sortedUsers.map((user, index) => {
                const isBlocked =
                  user.lockedUntil && new Date(user.lockedUntil) > new Date();
                return (
                  <tr key={user._id}>
                    <td data-label="No">{index + 1}</td>
                    <td data-label="Email" className="email-cell">{user.email}</td>
                    <td data-label="Index / ID" className="superadmin-id-cell">
                      <div>{user.index || "-"}</div>
                      <div className="superadmin-id-secondary">{user._id}</div>
                    </td>
                    <td data-label="Status">
                      <span
                        className={`superadmin-status-badge superadmin-status-${
                          isBlocked
                            ? "blocked"
                            : !user.isActive
                            ? "blocked"
                            : "active"
                        }`}
                      >
                        {isBlocked
                          ? "Blocked"
                          : !user.isActive
                          ? "Deactivated"
                          : "Active"}
                      </span>
                    </td>
                    <td data-label="Created">{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td data-label="Last Login">
                      {user.lastLoginAt
                        ? new Date(user.lastLoginAt).toLocaleDateString()
                        : "Never"}
                    </td>
                    <td data-label="Role">
                      <span
                        className={`superadmin-role-badge superadmin-role-${user.role}`}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td data-label="Change Role">
                      {user.role === "admin" ? (
                        isBlocked ? (
                          <Button
                            className="button-btn-light-blue"
                            onClick={() =>
                              addToast(
                                "Unblock admin to change role",
                                "error-warning"
                              )
                            }
                          >
                            Make SuperAdmin
                          </Button>
                        ) : (
                          <Button
                            className="button-btn-light-blue"
                            onClick={() =>
                              handleRoleChange(user._id, "superadmin")
                            }
                          >
                            Make SuperAdmin
                          </Button>
                        )
                      ) : (
                        <span className="superadmin-status-badge superadmin-status-protected">
                          Protected
                        </span>
                      )}
                    </td>
                    <td data-label="Block / Unblock">
                      {user.role === "superadmin" ? (
                        <span className="superadmin-status-badge superadmin-status-protected">
                          Protected
                        </span>
                      ) : !user.isActive ? (
                        <span className="superadmin-status-badge superadmin-status-protected">
                          Disabled
                        </span>
                      ) : (
                        <Button
                          className={
                            isBlocked
                              ? "button-btn-light-green"
                              : "button-btn-light-red"
                          }
                          onClick={() => handleBlockToggle(user._id, isBlocked)}
                        >
                          {isBlocked ? "Unblock" : "Block"}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SuperAdmin;
